import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { ContextMenu } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import ArchiveGoalModal from './ArchiveGoalModal';
import DeleteGoalModal from './DeleteGoalModal';
import UnarchiveGoalModal from './UnarchiveGoalModal';
import { useGoalActions } from './useGoalActions';

interface IGoalOptionsMenuProps {
  /** Trigger style: 'icon' on list rows, 'icon-outlined' on the detail page. */
  buttonType: 'icon' | 'icon-outlined';
  goal: Goal;
  /** Called after a successful delete, e.g. to navigate away from the detail page. */
  onDeleteSuccess?: () => void;
  onEdit: () => void;
}

function GoalOptionsMenu({
  buttonType,
  goal,
  onDeleteSuccess,
  onEdit,
}: Readonly<IGoalOptionsMenuProps>): JSX.Element {
  const { t } = useTranslation();

  const isArchived = goal.get('is_archived');
  // TODO(#7382): use the goal's real conversion count once conversion stats land.
  const totalConversions = 0;
  const {
    canDelete,
    isArchiveOpen,
    isDeleteOpen,
    isUnarchiveOpen,
    closeArchive,
    closeDelete,
    closeUnarchive,
    openArchive,
    openDelete,
    openUnarchive,
  } = useGoalActions(totalConversions);

  return (
    <>
      <ContextMenu buttonType={buttonType} name={'goal-options'} position={'left-start'}>
        <ContextMenu.Item name={'edit-goal'} onClick={onEdit} prefix={getIconForAction('edit')}>
          {t('Edit')}
        </ContextMenu.Item>
        {isArchived ? (
          <ContextMenu.Item
            name={'unarchive-goal'}
            onClick={openUnarchive}
            prefix={getIconForAction('archive')}
          >
            {t('Unarchive')}
          </ContextMenu.Item>
        ) : (
          <ContextMenu.Item
            name={'archive-goal'}
            onClick={openArchive}
            prefix={getIconForAction('archive')}
          >
            {t('Archive')}
          </ContextMenu.Item>
        )}
        <ContextMenu.Item
          disabled={!canDelete}
          name={'delete-goal'}
          onClick={openDelete}
          prefix={getIconForAction('delete')}
          theme={'danger'}
          tooltip={
            canDelete
              ? undefined
              : t('Goals with logged conversions must be archived to preserve historical data.')
          }
        >
          {t('Delete')}
        </ContextMenu.Item>
      </ContextMenu>
      {isArchived ? (
        <UnarchiveGoalModal goal={goal} onClose={closeUnarchive} opened={isUnarchiveOpen} />
      ) : (
        <ArchiveGoalModal goal={goal} onClose={closeArchive} opened={isArchiveOpen} />
      )}
      <DeleteGoalModal
        goal={goal}
        onClose={closeDelete}
        onSuccess={onDeleteSuccess}
        opened={isDeleteOpen}
      />
    </>
  );
}

export default observer(GoalOptionsMenu);
